import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Mail,
  Pause,
  Play,
  Building2,
  Calendar,
  TrendingUp,
  FileText,
  ArrowUpRight,
  Loader2,
  Save,
  ChevronRight,
  User,
  Clock,
  CheckCircle,
  ExternalLink,
} from "lucide-react";
import { fetchPartner, updatePartner } from "../api/partners";

// Status Badge Component
const StatusBadge = ({ status }) => {
  const isActive = status === "active";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
        isActive
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
          : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
      }`}
    >
      {isActive ? "Active" : "Paused"}
    </span>
  );
};

// Stat Item Component
const StatItem = ({ icon: Icon, label, value, color = "slate" }) => {
  const colorClasses = {
    slate: "text-slate-600 dark:text-slate-400",
    emerald: "text-emerald-600 dark:text-emerald-400",
    sky: "text-sky-600 dark:text-sky-400",
    amber: "text-amber-600 dark:text-amber-400",
  };

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50">
      <div className={`p-2 rounded-lg bg-white dark:bg-slate-800 ${colorClasses[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-slate-900 dark:text-white">{value}</p>
      </div>
    </div>
  );
};

// Lead Item Component
const LeadItem = ({ lead, onNavigate }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer group"
      onClick={() => onNavigate?.(lead.caseId)}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-sky-600 dark:text-sky-400" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
            {lead.name || lead.borrowerName || "Unknown"}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {formatDate(lead.accessedAt)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {lead.converted && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Converted
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors" />
      </div>
    </div>
  );
};

const PartnerDetailPanel = ({
  partner,
  onClose,
  onPause,
  onResume,
  onUpdate,
  onNavigateToCase,
}) => {
  const [activeTab, setActiveTab] = useState("info");
  const [partnerData, setPartnerData] = useState(null);
  const [leads, setLeads] = useState([]);
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [notesChanged, setNotesChanged] = useState(false);

  // Fetch full partner data when selected
  useEffect(() => {
    if (!partner) {
      setPartnerData(null);
      setLeads([]);
      setNotes("");
      setNotesChanged(false);
      return;
    }

    const fetchFullData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchPartner(partner.email, true);
        const data = response?.data || response;
        setPartnerData(data);
        setLeads(data?.leads || []);
        setNotes(data?.notes || "");
      } catch (err) {
        console.error("Failed to fetch partner details:", err);
        // Fall back to basic data
        setPartnerData(partner);
        setNotes(partner.notes || "");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFullData();
  }, [partner]);

  const handleNotesChange = (value) => {
    setNotes(value);
    setNotesChanged(value !== (partnerData?.notes || ""));
  };

  const handleSaveNotes = async () => {
    if (!partnerData) return;

    setIsSaving(true);
    try {
      await updatePartner(partnerData.email, { notes });
      setPartnerData(prev => ({ ...prev, notes }));
      setNotesChanged(false);
      onUpdate?.({ ...partnerData, notes });
    } catch (err) {
      console.error("Failed to save notes:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePauseResume = async () => {
    if (!partnerData) return;

    setActionLoading(true);
    try {
      if (partnerData.status === "active") {
        await onPause?.(partnerData.email);
        setPartnerData(prev => ({ ...prev, status: "paused" }));
      } else {
        await onResume?.(partnerData.email);
        setPartnerData(prev => ({ ...prev, status: "active" }));
      }
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const conversionRate =
    partnerData?.leadsAccessed > 0
      ? ((partnerData.leadsConverted / partnerData.leadsAccessed) * 100).toFixed(1)
      : 0;

  const tabs = [
    { id: "info", label: "Info" },
    { id: "leads", label: "Leads", count: leads.length },
    { id: "notes", label: "Notes" },
  ];

  return (
    <AnimatePresence>
      {partner && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-2xl flex flex-col"
          >
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-sky-500 animate-spin" />
              </div>
            ) : partnerData ? (
              <>
                {/* Header */}
                <div className="p-6 border-b border-slate-200 dark:border-slate-800">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-xl font-bold text-white flex-shrink-0">
                        {partnerData.name?.charAt(0)?.toUpperCase() ||
                          partnerData.email?.charAt(0)?.toUpperCase() ||
                          "P"}
                      </div>
                      <div className="min-w-0">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white truncate">
                          {partnerData.name || partnerData.email}
                        </h2>
                        {partnerData.company && (
                          <p className="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <Building2 className="w-3.5 h-3.5" />
                            {partnerData.company}
                          </p>
                        )}
                        <StatusBadge status={partnerData.status} />
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => window.open(`mailto:${partnerData.email}`)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-medium transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Email
                    </button>
                    <button
                      onClick={handlePauseResume}
                      disabled={actionLoading}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        partnerData.status === "active"
                          ? "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50"
                          : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50"
                      } disabled:opacity-50`}
                    >
                      {actionLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : partnerData.status === "active" ? (
                        <>
                          <Pause className="w-4 h-4" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4" />
                          Resume
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-200 dark:border-slate-800 px-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`relative pb-3 pt-2 px-4 text-sm font-medium transition-colors ${
                        activeTab === tab.id
                          ? "text-sky-600 dark:text-sky-400"
                          : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        {tab.label}
                        {tab.count !== undefined && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                            {tab.count}
                          </span>
                        )}
                      </span>
                      {activeTab === tab.id && (
                        <motion.div
                          layoutId="tab-indicator"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-600 dark:bg-sky-400"
                        />
                      )}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {activeTab === "info" && (
                    <div className="space-y-4">
                      {/* Contact Info */}
                      <div>
                        <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                          Contact Info
                        </h3>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <a
                              href={`mailto:${partnerData.email}`}
                              className="text-sky-600 dark:text-sky-400 hover:underline"
                            >
                              {partnerData.email}
                            </a>
                          </div>
                          {partnerData.company && (
                            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              {partnerData.company}
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            Partner since {formatDate(partnerData.createdAt)}
                          </div>
                        </div>
                      </div>

                      {/* Performance Stats */}
                      <div>
                        <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">
                          Performance
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          <StatItem
                            icon={ArrowUpRight}
                            label="Leads Accessed"
                            value={partnerData.leadsAccessed || 0}
                            color="sky"
                          />
                          <StatItem
                            icon={CheckCircle}
                            label="Leads Converted"
                            value={partnerData.leadsConverted || 0}
                            color="emerald"
                          />
                          <StatItem
                            icon={TrendingUp}
                            label="Conversion Rate"
                            value={`${conversionRate}%`}
                            color="amber"
                          />
                          <StatItem
                            icon={Clock}
                            label="Last Active"
                            value={partnerData.lastActiveAt ? formatDate(partnerData.lastActiveAt) : "Never"}
                            color="slate"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "leads" && (
                    <div className="space-y-3">
                      {leads.length === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
                            <User className="w-6 h-6 text-slate-400" />
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            No leads accessed yet
                          </p>
                        </div>
                      ) : (
                        leads.map((lead, index) => (
                          <LeadItem
                            key={lead.id || index}
                            lead={lead}
                            onNavigate={onNavigateToCase}
                          />
                        ))
                      )}
                    </div>
                  )}

                  {activeTab === "notes" && (
                    <div className="space-y-4">
                      <textarea
                        value={notes}
                        onChange={(e) => handleNotesChange(e.target.value)}
                        placeholder="Add notes about this partner..."
                        rows={8}
                        className="w-full px-4 py-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none"
                      />
                      <button
                        onClick={handleSaveNotes}
                        disabled={!notesChanged || isSaving}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Save Notes
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PartnerDetailPanel;
